import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function CsAndItTable9({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) return <p>No data available</p>;

  // Prepare Chart Data
  const years = data.map((d) => d.year);
  const employmentValues = data.map((d) => Number(d.age_45_54));
  const growthValues = data.map((d) =>
    d.growth_rate ? Number(d.growth_rate) : null
  );

  // Summary Values
  const firstYearTotal = employmentValues[0];
  const lastYearTotal = employmentValues[employmentValues.length - 1];
  const growthPercent = (
    ((lastYearTotal - firstYearTotal) / firstYearTotal) *
    100
  ).toFixed(1);

  const peakYear = data.reduce(
    (max, row) =>
      Number(row.age_45_54) > max.total
        ? { year: row.year, total: row.age_45_54 }
        : max,
    { year: data[0].year, total: employmentValues[0] }
  );

  // Chart Configuration
  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Employment (45–54)",
        data: employmentValues,
        borderColor: "#B5EAD6",
        backgroundColor: "#B5EAD630",
        fill: true,
        borderWidth: 2,
        pointRadius: 5,
        tension: 0.35,
        yAxisID: "y1",
      },
      {
        label: "Growth Rate (%)",
        data: growthValues,
        borderColor: "#999933",
        backgroundColor: "#999933",
        borderWidth: 2,
        pointRadius: 5,
        tension: 0.35,
        yAxisID: "y2",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📈 Employment Growth Trend (45–54 Age Group)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      y1: {
        title: { display: true, text: "Employment Count", font: { size: 14, weight: "bold" } },
        beginAtZero: false,
      },
      y2: {
        position: "right",
        title: { display: true, text: "Growth Rate (%)", font: { size: 14, weight: "bold" } },
        ticks: { callback: (value) => `${value}%` },
        grid: { drawOnChartArea: false },
      },
      x: { grid: { display: false } },
    },
  };

  // PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("cs_it_emp_45-55.pdf");
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-lg border border-gray-200"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment & Growth (Age 45–54) – 2018 to 2024
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm">Overall Growth</p>
            <p className="text-xl font-bold text-blue-700">+{growthPercent}%</p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm">Peak Year</p>
            <p className="text-xl font-bold text-yellow-700">{peakYear.year}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm">2024 Employment</p>
            <p className="text-xl font-bold text-green-700">
              {lastYearTotal.toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>Employment</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>Growth (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr
                      key={row.year}
                      className={`${
                        row.year === 2024
                          ? "bg-green-100 font-bold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2 text-left">{row.year}</td>
                      <td className="p-2" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(row.age_45_54).toLocaleString("en-US")}
                      </td>
                      <td
                        className="p-2 font-bold"
                        style={{
                          color: row.growth_rate >= 0 ? "#0142B2" : "#CB0C0C",
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums'
                        }}
                      >
                        {row.growth_rate ? `${row.growth_rate}%` : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg"
        >
          Download as PDF
          <img src={pdfIcon} alt="PDF icon" className="w-6" />
        </button>
      </div>
    </>
  );
}
